<template>
  <div>
    <v-toolbar class="fixed-bar">
      <v-btn
        class="mr-3"
        title="Back to Library"
        aria-label="Back to Library"
        @click="$router.push('/stdizer/groups')"
        v-b-tooltip
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>

      <v-spacer />

      <v-switch
        v-model="showDetails"
        class="mt-5 mr-4"
        label="Details"
      />

      <v-btn-toggle>
        <v-btn
          :title="group ? `Export group '${group.value}' to JSON` : ''"
          :aria-label="group ? `Export group '${group.value}' to JSON` : ''"
          @click="exportFile('JSON')"
          v-b-tooltip
        >
          <v-icon>mdi-file-download-outline</v-icon>
        </v-btn>

        <FileUploadDialog
          v-if="group && !group.frozen"
          button-title="mdi-file-replace-outline"
          @uploaded="onFileUploaded"
          label="JSON file"
          :title="`Replace group '${group.value}' from JSON file`"
          warning="This will replace content of this group. Are you sure?"
          :upload-url="`${STDIZER_URL}/groups/${this.groupId}/replace`"
        />

        <FileUploadDialog
          v-if="group && !group.frozen"
          button-title="mdi-file-upload-outline"
          @uploaded="onFileUploaded"
          title="Add operations from SDF, RDF, XLSX or CSV file"
          show-operations
          :preflight-url="`${SERVICES_URL}/preflight`"
          :upload-url="`${STDIZER_URL}/groups/${this.groupId}/add`"
        />

        <PromptDialog
          v-if="group && !group.frozen"
          v-model="visibleCopy"
          :visible="false"
          :text="editingItem ? editingItem.value : null"
          title="Do you want to copy?"
          @confirmed="copyItem"
        />

        <DeleteDialog
          v-if="group && !group.frozen"
          v-model="visibleDelete"
          :disabled="!selected.length"
          :items="selected"
          :visible="false"
          button-title="mdi-delete"
          title="Delete selected actions"
          no-server-delete
          @click="deleteItems(selected)"
          @itemDeleted="removeItem"
        />

        <OperationEditor
          v-if="group && !group.frozen"
          v-model="visibleEdit"
          :item="editingItem"
          @onSave="itemUpdated"
          :on-button-activate="resetEditingItem"
          button-title="mdi-playlist-plus"
          title="Add operation"
          max-width="600"
        />

        <v-btn
          :title="group && group.frozen ? `Thaw '${group.value}' group` : group ? `Freeze '${group.value}' group` : ''"
          :aria-label="group && group.frozen ? `Thaw ${group.value} group` : group ? `Freeze ${group.value} group` : ''"
          @click="freezeMeltGroup()"
          v-b-tooltip
        >
          <v-icon v-if="group && group.frozen">mdi-snowflake-melt</v-icon>
          <v-icon v-else>mdi-snowflake</v-icon>
        </v-btn>

        <ACLDialog
          v-if="group?.id && canACL(group)"
          :item="group"
          type="groups"
          :api-url="STDIZER_URL"
          button-title="mdi-account-key"
          title="Access Control"
          @aclUpdated="onAclUpdated"
        />
      </v-btn-toggle>
    </v-toolbar>

    <v-data-table
      v-model="selected"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, 100, -1],
      }"
      :headers="headers"
      :hide-default-footer="items.length <= 10"
      :items="items"
      :items-per-page="100"
      :search="search"
      item-key="key"
    >
      <template #[`item.type`]="{ item }">
        {{ item['@c'].substring(1) }}
      </template>

      <template #[`item.subType`]="{ item }">
        {{ item.type }}
      </template>

      <template #[`item.value`]="{ item }">
        <img
          v-if="item.type === 'SMIRKS'"
          :src="`${STDIZER_URL}/render?smirks=${encodeURIComponent(item.value)}`"
          alt=""
          style="width: 300px; height: 150px"
        />
        <img
          v-else-if="item.type === 'SMARTS' || item.type === 'SMILES'"
          :src="`${STDIZER_URL}/render?smiles=${encodeURIComponent(item.value)}`"
          alt=""
          style="width: 150px; height: 150px"
        />
        <template v-else-if="item['@c'] === '.GroupReference'">
          <span v-if="item.invalid">
            {{ item.value }}
          </span>
          <b-link
            v-else
            :to="`/stdizer/groups/${encodeURIComponent(item.value)}`"
          >
            {{ item.value }}
          </b-link>
        </template>
        <span v-else>{{ item.value }}</span>
        <div
          v-if="showDetails && ['SMIRKS', 'SMILES', 'SMARTS'].includes(item.type)"
          style="font-size: smaller"
        >
          {{ item.value }}
        </div>
      </template>

      <template #[`item.severity`]="{ item }">
        <v-chip
          v-if="item.severity"
          outlined
        >
          {{ item.severity }}
        </v-chip>
      </template>

      <template #[`item.action`]="{ item }">
        <v-chip
          v-if="item.action"
          outlined
        >
          {{ item.action }}
        </v-chip>
      </template>

      <template #[`item.flag`]="{ item }">
        <v-chip
          v-if="item.flag"
          outlined
          label
        >
          {{ item.flag }}
        </v-chip>
      </template>

      <template #[`item.title`]="{ item }">
        <b-link
          v-if="item['@c'] === '.GroupReference'"
          :to="`/stdizer/groups/${encodeURIComponent(item.value)}`"
        >
          {{ item.title }}
        </b-link>
        <span v-else>{{ item.title }}</span>
      </template>

      <template #[`item.status`]="{ item }">
        <v-icon
          v-if="item.frozen"
          title="Frozen"
          style="color: deepskyblue"
          v-b-tooltip
        >
          mdi-snowflake
        </v-icon>
        <v-icon
          v-if="item.invalid"
          :title="item.invalidMessage"
          color="red"
          v-b-tooltip
        >
          mdi-alert-circle-outline
        </v-icon>
      </template>

      <template #[`item.actions`]="{ item }">
        <div v-if="group && !group.frozen">
          <v-icon
            v-if="items.indexOf(item) !== 0"
            title="Move Up"
            @click="moveUp(item)"
            class="ma-1"
          >
            mdi-arrow-up
          </v-icon>
          <v-icon
            v-if="items.indexOf(item) !== items.length - 1"
            title="Move Down"
            @click="moveDown(item)"
            class="ma-1"
          >
            mdi-arrow-down
          </v-icon>
          <v-icon
            title="Edit"
            @click="editItem(item)"
            class="ma-1"
          >
            mdi-playlist-edit
          </v-icon>
          <v-icon
            title="Copy"
            @click="confirmCopyItem(item)"
            class="ma-1"
          >
            mdi-content-copy
          </v-icon>
          <v-icon
            title="Delete"
            @click="deleteItems([item])"
            class="ma-1"
          >
            mdi-delete
          </v-icon>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { SERVICES_URL, download, STDIZER_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import DeleteDialog from '@/components/dialogs/DeleteDialog.vue'
import OperationEditor from '@/components/stdizer/OperationEditor'
import FileUploadDialog from '@/components/dialogs/FileUploadDialog.vue'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import OperationsGroupBase from '@/components/stdizer/OperationsGroupBase'
import ACLDialog from '@/components/auth/ACLDialog.vue'
import AuthBase from '@/components/auth/AuthBase.vue'

function move(arr, from, to) {
  arr.splice(to, 0, arr.splice(from, 1)[0])
}

export default {
  mixins: [OperationsGroupBase, AuthBase],

  components: {
    ACLDialog,
    PromptDialog,
    DeleteDialog,
    OperationEditor,
    FileUploadDialog,
  },

  data: () => ({
    group: null,
    showDetails: false,
    allHeaders: [
      { text: 'Class', value: 'type' },
      { text: 'Type', value: 'subType' },
      { text: 'Value', value: 'value' },
      { text: 'Status', value: 'status' },
      { text: 'Severity', value: 'severity' },
      { text: 'Action', value: 'action' },
      { text: 'Flag', value: 'flag' },
      { text: 'Title', value: 'text' },
      { text: 'Description', value: 'description' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
    ],
  }),

  computed: {
    SERVICES_URL() {
      return SERVICES_URL
    },
    groupId() {
      return this.$route.params?.id
    },
    hasChecks() {
      return !!this.items.find((i) => i['@c'] === '.Check')
    },
    headers() {
      let _headers = this.allHeaders
      if (!this.hasChecks) _headers = _headers.filter((h) => h.value !== 'severity' && h.value !== 'action')
      if (this.group && this.group.frozen) _headers = _headers.filter((h) => h.value !== 'actions')
      return _headers
    },
  },

  watch: {
    groupId() {
      this.getData()
    },
  },

  methods: {
    onAclUpdated(acl) {
      this.group.acl = acl
    },
    freezeMeltGroup() {
      this.$axios
        .patch(`${STDIZER_URL}/groups/${this.groupId}/freeze?freeze=${!this.group.frozen}`)
        .then(() => (this.group.frozen = !this.group.frozen))
    },
    removeItem(item) {
      this.items.splice(this.items.indexOf(item), 1)
      this.updateDatabase()
    },
    copyItem() {
      let i = this.items.indexOf(this.editingItem)
      let item = Object.assign({}, this.editingItem)
      item.key = this.maxKey++
      this.items.splice(i + 1, 0, item)
      this.updateDatabase()
    },
    exportFile(format) {
      format = format.toLowerCase()
      this.$axios
        .get(`${STDIZER_URL}/groups/${this.groupId}/recursive`, {
          responseType: 'arraybuffer',
        })
        .then((response) => {
          let filename = `${this.group.value}.${format.toLowerCase()}`
          download(filename, response.data, format)
        })
        .catch((error) => {
          console.error('Error during export:', error)
          showErrorToast(error, { title: 'Error during export' })
        })
    },
    moveUp(item) {
      let i = this.items.indexOf(item)
      if (i > 0) {
        move(this.items, i, i - 1)
        this.updateDatabase()
      }
    },
    moveDown(item) {
      let i = this.items.indexOf(item)
      if (i < this.items.length - 1) {
        move(this.items, i, i + 1)
        this.updateDatabase()
      }
    },
    updateDatabase() {
      this.$axios.put(`${STDIZER_URL}/groups/${this.groupId}`, this.group)
    },
    onFileUploaded(group) {
      group.operations.forEach((v, i) => (v.key = i))
      this.group = group
      this.items = this.group.operations
      this.maxKey = this.items.length
    },
    getData() {
      this.$axios.get(`${STDIZER_URL}/groups/${this.groupId}`).then((response) => {
        response.data.operations.forEach((v, i) => (v.key = i))
        this.group = response.data
        this.items = this.group.operations
        this.maxKey = this.items.length
      })
    },
  },
}
</script>

<style scoped></style>
